import React from 'react';
import type { SortDir } from '../../lib/types';
import SortableHeader from './SortableHeader';

/**
 * One column of a Devices page table: what it is called, how wide it is, and — when it can
 * be clicked to sort — which key that means.
 *
 * `width` is a percentage string. Every table's widths must sum to 100%, with the trailing
 * Action column at `ACTION_COLUMN_WIDTH` so the tables line up down the page; see
 * `columnWidths.ts`, and the invariant test in `test/rescue/tableColumnWidths.test.ts`.
 */
export interface TableColumn<K extends string = string> {
  label: string;
  width: string;
  sortKey?: K;
  /**
   * Leave the header cell visually empty. For a column whose own control names it — the
   * "Actions" menu trigger — a heading only repeats what the button already says.
   *
   * `label` is still required and still has to be distinct: it stays the column's identity
   * (the React key) and becomes the header's accessible name, so the column is skipped on
   * screen but not in a screen reader. Not combinable with `sortKey` — a sortable column
   * needs a visible affordance to click.
   */
  hideLabel?: boolean;
}

interface TableHeadProps<K extends string> {
  columns: readonly TableColumn<K>[];
  /** The column currently sorted, or null when none is. */
  sortKey?: K | null;
  sortDir?: SortDir;
  /** Omit to render every header plain, even ones that declare a `sortKey`. */
  onSort?: (key: K) => void;
}

/**
 * The `<colgroup>` and `<thead>` for a table, both driven off one column list.
 *
 * They are rendered together deliberately. Written out separately — as these tables used to
 * — the widths and the header cells are two hand-maintained lists that have to stay in the
 * same order, and nothing catches it when they don't: the columns simply come out the wrong
 * width, with no error. Returning both from one array makes that drift unrepresentable.
 */
export default function TableHead<K extends string>({ columns, sortKey, sortDir, onSort }: TableHeadProps<K>) {
  return (
    <>
      <colgroup>
        {columns.map((col) => (
          <col key={col.label} style={{ width: col.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((col) => {
            const key = col.sortKey;
            // Checked before sortability: the two don't combine, and a hidden label would
            // leave a sort button with nothing to read.
            if (col.hideLabel) {
              return <th key={col.label} aria-label={col.label} />;
            }
            // Sortable only when the column declares a key AND the table opted in with a handler.
            if (!key || !onSort) {
              return <th key={col.label}>{col.label}</th>;
            }
            return (
              <SortableHeader
                key={col.label}
                label={col.label}
                active={sortKey === key}
                dir={sortDir}
                onClick={() => onSort(key)}
              />
            );
          })}
        </tr>
      </thead>
    </>
  );
}
